import { ChevronDown, Globe, Minus, Plus, Search } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { RESIDENCY_OPTIONS, residencyName } from '~/lib/residency';
import { colors, control, fontFamily, radii, shadows, space, type } from '~/ui/tokens';
import { CountryFlag } from './ui/CountryFlag';
import {
  MAX_ADULTS_PER_ROOM,
  MAX_CHILDREN_PER_ROOM,
  MAX_CHILD_AGE,
  MAX_ROOMS,
  type Occupancy,
} from '../lib/occupancy';
import { guestsFromOccupancy, type GuestsSelection } from '../lib/guests';

interface GuestsPanelProps {
  visible: boolean;
  value: GuestsSelection;
  onChange: (value: GuestsSelection) => void;
  onClose: () => void;
  nationality: string;
  suggestedNationality: string;
  onNationalityChange: (code: string) => void;
  promptNationality?: boolean;
  /** Hide the nationality section when the host surface has its own nationality field. */
  showNationality?: boolean;
}

const DEFAULT_CHILD_AGE = 8;

function resizeChildren(children: number[], count: number): number[] {
  if (count <= children.length) return children.slice(0, count);
  return [...children, ...Array.from({ length: count - children.length }, () => DEFAULT_CHILD_AGE)];
}

function fallbackOccupancy(value: GuestsSelection): Occupancy {
  if (value.occupancy?.length) return value.occupancy;
  const rooms = Math.max(1, Math.min(MAX_ROOMS, value.rooms));
  const adults = Math.max(1, value.adults);
  const base = Math.floor(adults / rooms);
  const remainder = adults % rooms;
  return Array.from({ length: rooms }, (_, index) => ({
    adults: Math.max(1, base + (index < remainder ? 1 : 0)),
    children: index === 0 ? Array.from({ length: value.children }, () => DEFAULT_CHILD_AGE).slice(0, MAX_CHILDREN_PER_ROOM) : [],
  }));
}

function shortCountryCode(code: string): string {
  return ({ in: 'IND', gb: 'UK' } as Record<string, string>)[code] ?? code.toUpperCase();
}

export function GuestsPanel({ visible, value, onChange, onClose, nationality, suggestedNationality, onNationalityChange, promptNationality = false, showNationality = true }: GuestsPanelProps) {
  const [countryOpen, setCountryOpen] = useState(promptNationality);
  const [query, setQuery] = useState('');
  const countryMenuRef = useRef<View>(null);
  const displayedCountry = nationality || suggestedNationality;
  const matchingCountries = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term
      ? RESIDENCY_OPTIONS.filter(({ code, name }) => name.toLowerCase().includes(term) || code.includes(term) || shortCountryCode(code).toLowerCase().includes(term))
      : RESIDENCY_OPTIONS;
  }, [query]);

  useEffect(() => {
    if (promptNationality) setCountryOpen(true);
  }, [promptNationality]);

  useEffect(() => {
    if (!countryOpen || typeof requestAnimationFrame === 'undefined') return;
    const frame = requestAnimationFrame(() => {
      (countryMenuRef.current as unknown as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [countryOpen]);

  if (!visible) return null;
  const occupancy = fallbackOccupancy(value);

  function update(next: Occupancy) {
    onChange(guestsFromOccupancy(next));
  }

  function setRoom(index: number, room: Occupancy[number]) {
    update(occupancy.map((current, roomIndex) => roomIndex === index ? room : current));
  }

  function selectNationality(code: string) {
    onNationalityChange(code);
    setCountryOpen(false);
    setQuery('');
  }

  return (
    <View style={styles.panel}>
      <View style={styles.heading}>
        <Text style={styles.title}>Guests & rooms</Text>
        <Text style={styles.subtitle}>{occupancy.some((room) => room.children.length > 0) ? 'Select each child age · same room type' : 'Same room type · up to 3 rooms'}</Text>
      </View>

      {showNationality ? (
      <View style={styles.nationality}>
        <View style={styles.nationalityHead}><Globe size={16} color={colors.brand} /><Text style={styles.roomTitle}>Lead guest nationality</Text></View>
        <View style={styles.stepper}>
          <Text style={styles.stepperLabel}>Citizenship</Text>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={nationality ? `Lead guest nationality: ${residencyName(nationality)}` : `Suggested lead guest nationality: ${residencyName(displayedCountry)}. Select to confirm.`}
            accessibilityState={{ expanded: countryOpen }}
            onPress={() => { setQuery(''); setCountryOpen((current) => !current); }}
            style={styles.countryTrigger}
          >
            <CountryFlag code={displayedCountry} size={16} />
            <Text style={styles.countryCode}>{shortCountryCode(displayedCountry)}</Text>
            <ChevronDown size={12} color={colors.inkFaint} />
          </Pressable>
        </View>
        {countryOpen ? (
          <View ref={countryMenuRef} style={styles.countryMenu}>
            <View style={styles.countrySearch}>
              <Search size={14} color={colors.inkFaint} />
              <TextInput
                value={query}
                onChangeText={setQuery}
                onSubmitEditing={() => {
                  const term = query.trim().toLowerCase();
                  const exact = matchingCountries.find(({ code, name }) => code === term || name.toLowerCase() === term || shortCountryCode(code).toLowerCase() === term);
                  const match = exact ?? (matchingCountries.length === 1 ? matchingCountries[0] : null);
                  if (match) selectNationality(match.code);
                }}
                placeholder="Search country or code"
                placeholderTextColor={colors.inkFaint}
                accessibilityLabel="Search nationalities"
                autoFocus={promptNationality}
                style={styles.countrySearchInput}
              />
            </View>
            <ScrollView style={styles.countryList} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
              {matchingCountries.length ? matchingCountries.map(({ code, name }) => (
                <Pressable
                  key={code}
                  accessibilityRole="button"
                  accessibilityLabel={`${name}, ${shortCountryCode(code)}`}
                  onPress={() => selectNationality(code)}
                  style={[styles.countryOption, nationality === code && styles.countryOptionSelected]}
                >
                  <CountryFlag code={code} size={18} />
                  <Text numberOfLines={1} style={styles.countryName}>{name}</Text>
                  <Text style={styles.countryOptionCode}>{shortCountryCode(code)}</Text>
                </Pressable>
              )) : <Text style={styles.noCountries}>No countries found</Text>}
            </ScrollView>
          </View>
        ) : null}
      </View>
      ) : null}

      {occupancy.map((room, index) => (
        <View key={index} style={[styles.room, index > 0 && styles.roomDivided]}>
          <View style={styles.roomHeader}>
            <Text style={styles.roomTitle}>Room {index + 1}</Text>
            {occupancy.length > 1 ? (
              <Pressable accessibilityRole="button" onPress={() => update(occupancy.filter((_, roomIndex) => roomIndex !== index))}>
                <Text style={styles.remove}>Remove room</Text>
              </Pressable>
            ) : null}
          </View>
          <Stepper label="Adults" value={room.adults} min={1} max={MAX_ADULTS_PER_ROOM} onChange={(adults) => setRoom(index, { ...room, adults })} />
          <Stepper
            label="Children"
            hint="ages 0–17"
            value={room.children.length}
            min={0}
            max={MAX_CHILDREN_PER_ROOM}
            onChange={(count) => setRoom(index, { ...room, children: resizeChildren(room.children, count) })}
          />
          {room.children.map((age, childIndex) => (
            <Stepper
              key={index + '-' + childIndex}
              label={'Child ' + (childIndex + 1) + ' age'}
              value={age}
              min={0}
              max={MAX_CHILD_AGE}
              indented
              onChange={(nextAge) => setRoom(index, { ...room, children: room.children.map((currentAge, ageIndex) => ageIndex === childIndex ? nextAge : currentAge) })}
            />
          ))}
        </View>
      ))}

      <View style={styles.actions}>
        {occupancy.length < MAX_ROOMS ? (
          <Pressable accessibilityRole="button" onPress={() => update([...occupancy, { adults: 2, children: [] }])} style={styles.addButton}>
            <Plus size={15} color={colors.brand} />
            <Text style={styles.addText}>Add room</Text>
          </Pressable>
        ) : <View />}
        <Pressable accessibilityRole="button" onPress={onClose} style={styles.doneButton}>
          <Text style={styles.doneText}>Done</Text>
        </Pressable>
      </View>
    </View>
  );
}

function Stepper({ label, hint, value, min, max, onChange, indented }: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  indented?: boolean;
}) {
  return (
    <View style={[styles.stepper, indented && styles.stepperIndented]}>
      <View style={styles.stepperCopy}>
        <Text style={[styles.stepperLabel, indented && styles.stepperLabelSub]}>{label}</Text>
        {hint ? <Text style={styles.hint}>{hint}</Text> : null}
      </View>
      <View style={styles.controls}>
        <Pressable accessibilityRole="button" disabled={value <= min} onPress={() => onChange(value - 1)} style={[styles.stepButton, value <= min && styles.disabled]}>
          <Minus size={14} color={value <= min ? colors.inkFaint : colors.ink} />
        </Pressable>
        <Text style={styles.value}>{value}</Text>
        <Pressable accessibilityRole="button" disabled={value >= max} onPress={() => onChange(value + 1)} style={[styles.stepButton, value >= max && styles.disabled]}>
          <Plus size={14} color={value >= max ? colors.inkFaint : colors.ink} />
        </Pressable>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // No forced scroll: overflow:'scroll' created a square-cornered scroll
  // container inside the rounded popover, whose corners/scrollbar gutter showed
  // through the outline. The popover itself clips and grows to fit.
  panel: { width: '100%', backgroundColor: colors.card, padding: space.internal, gap: space.compact, ...({ boxSizing: 'border-box' } as object) },
  heading: { paddingBottom: space.related, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  title: { fontFamily: fontFamily.semibold, ...type.subsectionTitle, color: colors.ink },
  subtitle: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint, marginTop: space.related },
  room: { gap: space.related },
  // The panel's own `gap` owns the distance between rooms; this only adds the
  // breathing room under the divider rule.
  roomDivided: { borderTopWidth: 1, borderTopColor: colors.lineSoft, paddingTop: space.compact },
  roomHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: space.micro },
  roomTitle: { fontFamily: fontFamily.semibold, ...type.bodyRegular, color: colors.ink },
  nationality: { gap: space.related, paddingBottom: space.compact, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  nationalityHead: { flexDirection: 'row', alignItems: 'center', gap: space.related },
  countryTrigger: { height: 30, minWidth: 82, paddingHorizontal: 7, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 },
  countryCode: { fontFamily: fontFamily.semibold, ...type.metadata, color: colors.ink },
  countryMenu: { alignSelf: 'flex-end', width: 250, maxWidth: '100%', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, overflow: 'hidden', ...shadows.lift },
  countrySearch: { minHeight: 38, flexDirection: 'row', alignItems: 'center', gap: space.related, paddingHorizontal: space.compact, borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
  countrySearchInput: { flex: 1, minWidth: 0, height: 38, fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.ink, outlineStyle: 'none' } as object,
  countryList: { maxHeight: 120 },
  countryOption: { height: 40, flexDirection: 'row', alignItems: 'center', gap: space.related, paddingHorizontal: space.compact },
  countryOptionSelected: { backgroundColor: colors.lineSoft },
  countryName: { flex: 1, minWidth: 0, fontFamily: fontFamily.medium, ...type.bodyRegular, color: colors.ink },
  countryOptionCode: { fontFamily: fontFamily.medium, ...type.metadata, color: colors.inkSoft },
  noCountries: { padding: space.compact, fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkSoft },
  remove: { fontFamily: fontFamily.medium, ...control.utilityCta, color: colors.brand },
  stepper: { minHeight: 38, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  stepperIndented: { paddingLeft: space.compact },
  stepperCopy: { flexDirection: 'row', alignItems: 'baseline', gap: space.related },
  stepperLabel: { fontFamily: fontFamily.medium, ...type.bodyRegular, color: colors.inkSoft },
  stepperLabelSub: { ...type.bodyRegular, color: colors.inkFaint },
  hint: { fontFamily: fontFamily.regular, ...type.metadata, color: colors.inkFaint },
  controls: { flexDirection: 'row', alignItems: 'center', gap: space.compact },
  stepButton: { width: 30, height: 30, borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, alignItems: 'center', justifyContent: 'center' },
  disabled: { opacity: 0.45 },
  value: { minWidth: 22, textAlign: 'center', fontFamily: fontFamily.semibold, ...control.inputValue, color: colors.ink },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: space.compact, borderTopWidth: 1, borderTopColor: colors.lineSoft },
  addButton: { flexDirection: 'row', alignItems: 'center', gap: space.micro, paddingVertical: space.related },
  addText: { fontFamily: fontFamily.semibold, ...control.secondaryCta, color: colors.brand },
  doneButton: { backgroundColor: colors.brand, borderRadius: radii.sm, paddingHorizontal: space.card, paddingVertical: space.related },
  doneText: { fontFamily: fontFamily.semibold, ...control.secondaryCta, color: colors.white },
});
